<template>
  <v-card
    class="ma-0 px-1 app-form-page cmp-page"
    :class="{
      'app-form-page--fixed': !isMobile && !offlineSubmit,
      'cmp-page--mobile': isMobile,
      'theme--dark': $vuetify.theme.isDark,
    }"
    width="100%"
    flat
  >
    <!-- Mobile header -->
    <div
      v-if="isMobile && !offlineSubmit"
      class="amf-header"
    >
      <div class="amf-header__bar">
        <div class="amf-header__bar-actions">
          <button
            type="button"
            class="amf-header__btn"
            :aria-label="$t(type + 'List')"
            @click="goTo(type + 'List')"
          >
            <v-icon
              size="20"
              color="#37474F"
            >
              mdi-format-list-numbered
            </v-icon>
          </button>
        </div>
        <h1 class="amf-header__title">
          {{ $t($route.name) }}
        </h1>
        <button
          type="button"
          class="amf-header__btn amf-header__btn--back"
          :aria-label="$t('back')"
          @click="$router.go(-1)"
        >
          <v-icon
            size="22"
            color="#37474F"
          >
            mdi-arrow-left
          </v-icon>
        </button>
      </div>
    </div>

    <!-- Desktop toolbar -->
    <v-app-bar
      v-else-if="!offlineSubmit"
      flat
      class="my-0 app-form-toolbar mb-0 py-0 px-2"
      dense
      height="40"
    >
      <v-toolbar-title class="app-form-toolbar__title me-3">
        {{ $t($route.name) }}
      </v-toolbar-title>
      <v-spacer />
      <div class="app-form-toolbar__actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(type + 'List')"
            >
              <v-icon color="primary">
                mdi-format-list-numbered
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $t(type + 'List') }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="$router.go(-1)"
            >
              <v-icon color="primary">
                mdi-arrow-left
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $t('back') }}</span>
        </v-tooltip>
      </div>
    </v-app-bar>
    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />
    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="app-form-page__form cmp-form"
      lazy-validation
    >
      <div class="cmp-body">
        <!-- Header: party / date / currency -->
        <div class="app-form-card cmp-card cmp-head">
          <div class="app-form-card__header">
            <div
              class="app-form-card__icon"
              aria-hidden="true"
            >
              <v-icon
                color="primary"
                size="22"
              >
                {{ type === 'pay' ? 'mdi-cash-minus' : 'mdi-cash-plus' }}
              </v-icon>
            </div>
            <div class="app-form-card__heading">
              <div class="app-form-card__title">
                {{ $t('public-information') }}
              </div>
              <div class="app-form-card__subtitle">
                {{ $t('composite-header-hint') }}
              </div>
            </div>
          </div>
          <div class="app-form-card__body">
            <compositeheader
              v-model="formHeader"
              :type-prop="type"
            />
          </div>
        </div>

        <!-- Totals -->
        <section class="cmp-kpis">
          <div class="cmp-kpi cmp-kpi--green">
            <span class="cmp-kpi__icon"><v-icon size="16">mdi-cash</v-icon></span>
            <div class="cmp-kpi__text">
              <div class="cmp-kpi__top">
                <span class="cmp-kpi__label">{{ $t('sumCriticism') }}</span>
                <span class="cmp-kpi__meta">{{ cashCount }} {{ $t('composite-rows') }}</span>
              </div>
              <strong class="cmp-kpi__value">{{ $numberFormat(cashTotal) }} <small>{{ currencyName }}</small></strong>
            </div>
          </div>
          <div class="cmp-kpi cmp-kpi--amber">
            <span class="cmp-kpi__icon"><v-icon size="16">mdi-checkbook</v-icon></span>
            <div class="cmp-kpi__text">
              <div class="cmp-kpi__top">
                <span class="cmp-kpi__label">{{ $t('sumCheck') }}</span>
                <span class="cmp-kpi__meta">{{ checkCount }} {{ $t('composite-rows') }}</span>
              </div>
              <strong class="cmp-kpi__value">{{ $numberFormat(checkTotal) }} <small>{{ currencyName }}</small></strong>
            </div>
          </div>
          <div class="cmp-kpi cmp-kpi--blue">
            <span class="cmp-kpi__icon"><v-icon size="16">mdi-sigma</v-icon></span>
            <div class="cmp-kpi__text">
              <div class="cmp-kpi__top">
                <span class="cmp-kpi__label">{{ $t('composite-grand-total') }}</span>
                <span class="cmp-kpi__meta">{{ cashCount + checkCount }} {{ $t('composite-rows') }}</span>
              </div>
              <strong class="cmp-kpi__value">{{ $numberFormat(cashTotal + checkTotal) }} <small>{{ currencyName }}</small></strong>
            </div>
          </div>
        </section>

        <!-- Items: cash / cheque -->
        <div class="cmp-card cmp-items">
          <v-tabs
            class="cmp-tabs"
            height="32"
            hide-slider
          >
            <v-tab class="cmp-tab">
              <v-icon
                size="16"
                class="me-1"
              >
                mdi-cash
              </v-icon>
              {{ $t('criticism') }}
              <span
                v-if="cashCount"
                class="cmp-tab__count"
              >{{ cashCount }}</span>
            </v-tab>
            <v-tab class="cmp-tab">
              <v-icon
                size="16"
                class="me-1"
              >
                mdi-checkbook
              </v-icon>
              {{ $t('check') }}
              <span
                v-if="checkCount"
                class="cmp-tab__count"
              >{{ checkCount }}</span>
            </v-tab>
            <v-tab-item eager>
              <compositePaymentItems
                ref="refPaymentItems"
                v-model="paymentItems"
                :form-header="formHeader"
                :type="type"
              />
            </v-tab-item>
            <v-tab-item eager>
              <compositeCheckItems
                ref="refCheckItems"
                v-model="checkItems"
                :form-header="formHeader"
                :type="type + 'Check'"
              />
            </v-tab-item>
          </v-tabs>
        </div>
      </div>

      <!-- Fixed footer: submit at the start, grand total at the end -->
      <div class="app-form-page__actions cmp-footer">
        <div class="app-form-page__actions-inner">
          <v-btn
            v-if="!id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="create"
          >
            {{ $t('submit') }}
          </v-btn>
          <v-btn
            v-if="id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="update"
          >
            {{ $t('submit') }}
          </v-btn>
          <v-spacer />
          <div class="cmp-footer__total">
            <span>{{ $t('composite-grand-total') }}</span>
            <strong>{{ $numberFormat(cashTotal + checkTotal) }}</strong>
            <small>{{ currencyName }}</small>
          </div>
        </div>
      </div>
    </v-form>
    <v-dialog
      v-model="loadingDialog"
      hide-overlay
      persistent
      width="300"
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text>
          <p
            style="color: white"
            class="text-center"
          >
            {{ (!lodingText ? $t('syncData') : $t(lodingText)) }} ...
          </p>
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snakbar"
      :color="(snakbarColor ? snakbarColor : 'primary')"
      top
      style="z-index: 1000000000000 !important;"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snakbar = !snakbar"
        >
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
  // import autocomplete from '../../components/general/patial/autocomplete'
  import General from '../../../../Mixins/general'
  import compositeheader from './partial/compositeHeader.vue'
  import compositePaymentItems from './partial/compositePaymentItems.vue'
  import compositeCheckItems from './partial/compositeCheckItems.vue'
  export default {
    name: 'Customer',
    components: {
      // Combobox,
      // autocomplete,
      compositeCheckItems,
      compositeheader,
      compositePaymentItems,
    },
    mixins: [General],
    props: ['typeForm', 'accountProp', 'typeProp', 'notStore', 'offlineSubmit'],
    data: () => ({
      valid: true,
      dateFormat: 'YYYY/MM/DD',
      loadingDialog: false,
      formHeader: null,
      checkItems: null,
      paymentItems: null,
      paymentByPos: null,
      lodingText: null,
      more: true,
      currency: null,
      attachments: null,
      defaultCurrency: null,
      issueTracking: null,
      accountGiven: null,
      bankGiven: null,
      id: null,
      extra: false,
      relations: null,
      given: null,
      snakbar: null,
      snakbarText: null,
      snakbarColor: null,
      sumRelations: null,
      currency_amount: 1,
      tag: null,
      remianBankColor: 'blue',
      remianColor: 'blue',
      customerId: null,
      bankRemain: null,
      taxStatus: null,
      taxStatusItems: null,
      clearing: null,
      snackbarShow: null,
      amount: 0,
      editPrice: 0,
      type: null,
      NetPrice: 0,
      taxAmount: 0,
      tax: 9,
      discount: 0,
      taxFeilds: null,
      discountFeilds: null,
      account: null,
      options: {
        locale: 'en-US',
        prefix: '',
        suffix: '  ',
        precision: 0,
      },
      optionsTax: {
        locale: 'en-US',
        prefix: '',
        suffix: '  %  ',
        precision: 0,
      },
      detail: null,
      bank: null,
      remianLable: null,
      description: null,
      date: null,
      render: true,
      isLoading: false,
    }),
    computed: {
      cashTotal () {
        return (this.paymentItems && this.paymentItems.totalAmount) || 0
      },
      checkTotal () {
        return (this.checkItems && this.checkItems.totalAmount) || 0
      },
      cashCount () {
        return (this.paymentItems && this.paymentItems.items) ? this.paymentItems.items.length : 0
      },
      checkCount () {
        return (this.checkItems && this.checkItems.items) ? this.checkItems.items.length : 0
      },
      currencyName () {
        return (this.formHeader && this.formHeader.currency) ? this.formHeader.currency.name : ''
      },
      sum () {
        if (this.taxStatus === 'notAdd') {
          return parseFloat(this.price) - parseFloat(this.discount) + parseFloat(this.editPrice)
        } else {
          return parseFloat(this.price) - parseFloat(this.discount) + parseFloat(this.taxAmount)
        }
      },
    },
    watch: {
      paymentItems (val) {
      },
      formHeader (val) {
      },
      defaultCurrency () {
        if (this.defaultCurrency) {
          this.$refs.refCurrency.setModel(this.defaultCurrency)
          this.currency_amount = this.defaultCurrency.amount
        }
      },
      bank () {
        if (this.render) {
          this.bankRemainCheckPage()
        }
      },
      account (val) {
        this.remianLable = null
        this.remianColor = 'blue'
        if (this.account && this.account.remain && this.account.remain.remain > 0) {
          this.remianColor = 'pink'
          this.remianLable = this.$t('debtor')
        }
        if (this.account && this.account.remain && this.account.remain.remain < 0) {
          this.remianColor = 'green'
          this.remianLable = this.$t('creditor')
        }
        if (this.$refs.refRelations) {
          this.$refs.refRelations.initList = false
          this.relations = null
          this.$refs.refRelations.clear()
          this.$refs.refRelations.getItems()
        }
        if (val) {
          this.getPersonInfo(val.id)
        }
      },
      currency (val) {
        if (!this.id) {
          this.amount = 0
          this.bankRemain = 0
          this.currency_amount = (this.currency ? this.currency.amount : 1)
        } else {
          if (!val) {
            this.currency = this.$store.state.currency
            this.bank = null
          }
        }
      },
      relations () {
        var sum = 0
        this.relations.map(function (item) {
          sum = parseFloat(sum) + parseFloat(item.price)
        })
        this.sumRelations = sum
      },
    },
    beforeMount () {
      if (this.$route.name === 'compositeReceipt') {
        this.type = 'recieve'
      }
      if (this.$route.name === 'compositePayment') {
        this.type = 'pay'
      }
      this.setInitProp()
      if (this.$route.params.id != null && !this.accountProp) {
        this.show()
        this.id = this.$route.params.id
        this.render = false
      } else {
        this.render = true
        this.defaultCurrency = this.getDefault('defaultCurrency')
      }
      if (this.$store.state.currency && !this.id) {
        this.setInitCurrency()
      }
    },
    mounted () {
      this.setDate()
    },
    methods: {
      setInitProp () {
        if (this.accountProp) {
          this.account = this.accountProp
        }
        if (this.typeProp) {
          this.type = this.typeProp
        }
      },
      setInitCurrency () {
        this.currency = this.$store.state.currency
        this.currency_amount = this.$store.state.currency.amount
      },
      changeCurrency () {
        this.bankRemain = 0
        this.currency_amount = this.currency.amount
        this.options = {
          locale: 'en-US',
          prefix: '',
          suffix: this.currency.name,
          precision: 0,
        }
      },
      SetValueMaxPrice () {
        if (this.render && this.type === 'pay') {
          // this.snakbarText = this.$t('alert-pay-amount-bigger-than-bank-remain')
          // this.snakbarColor = 'pink'
          // this.snakbar = true
          // this.amount = 0
        }
      },
      bankRemainCheckPage () {
        if (this.render && this.bank && this.type === 'pay') {
          if (this.$route.params.id != null) {
            this.bankRemain = parseFloat((this.bank.currency_id === this.$store.state.currency.id ? this.bank.remain.remain : this.bank.remain.currency_remain)) + parseFloat(this.editPrice) - parseFloat(this.amount)
          } else {
            this.bankRemain = parseFloat((this.bank.currency_id === this.$store.state.currency.id ? this.bank.remain.remain : this.bank.remain.currency_remain)) - parseFloat(this.amount)
          }
          this.remianBankColor = 'grey'
          if (this.bankRemain > 0) {
            this.remianBankColor = 'green'
          }
          if (this.bankRemain < 0) {
            this.remianBankColor = 'pink'
          }
          if (this.bank && parseFloat(this.amount) > parseFloat((this.bank.currency_id === this.$store.state.currency.id ? this.bank.remain.remain : this.bank.remain.currency_remain)) + parseFloat(this.editPrice)) {
            this.amount = 0
            this.bankRemain = parseFloat((this.bank.currency_id === this.$store.state.currency.id ? this.bank.remain.remain : this.bank.remain.currency_remain)) + parseFloat(this.editPrice) - parseFloat(this.amount)
            this.snakbarText = this.$t('alert-pay-amount-bigger-than-bank-remain')
            this.snakbarColor = 'pink'
            this.snakbar = true
          }
        }
      },
      SetValueMaxDiscount () {
        this.discount = this.price
      },
      create () {
        if (!this.$refs.form.validate()) return false
        if (this.offlineSubmit) {
          this.$emit('input', this.$data)
          return false
        }
        // if (parseFloat(this.bank.remain) - parseFloat(this.price) < 0) {
        //   this.bankRemainCheckPage()
        //   return false
        // }
        var app = this
        if (this.notStore) {
          const copy = Object.assign({}, this.$data)
          app.$emit('get', copy)
          app.$emit('close')
          this.clearForm()
          this.setInitProp()
        } else {
          app.isLoading = true
          app.$axios.post(this.$endpoint + 'composite', this.$data)
            .then(res => {
              res = res.data
              app.snakbarText = app.$t('registered')
              app.snakbarColor = 'green'
              app.snakbar = true
              app.$refs.refPaymentItems.clearForm()
              app.$refs.refCheckItems.clearForm()
            })
            .catch(err => {
              this.serverErrorHandeler(err)
            })
            .finally(() => {
              this.isLoading = false
              // this.clearForm()
            })
        }
      },
      update () {
        var app = this
        app.isLoading = true
        app.$axios.put(this.$endpoint + 'payments/' + this.$route.params.id, this.$data)
          .then(res => {
            res = res.data
            if (app.$refs.refFilePicker !== undefined) {
              app.$refs.refFilePicker.create(res.data.id)
            }
            app.$emit('close', app.$route)
            app.goTo(app.type + 'List')
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => (app.isLoading = false))
      },
      show () {
        var app = this
        app.loading = true
        app.$axios.get(this.$endpoint + 'payments/' + this.$route.params.id + '/edit', {
          params: {
            id: this.$route.params.id,
          },
        })
          .then(res => {
            app.$nextTick(() => {
              res = res.data
              app.currency = res.currency
              app.currency_amount = res.currency_amount
              app.type = res.type
              app.bankRemain = parseFloat(res.bank.remain.currency_remain)
              app.id = res.id
              app.attachments = res.attachments
              app.issueTracking = res.issue_tracking
              app.branch = res.branch
              app.amount = res.amount
              app.editPrice = res.amount
              app.account = res.account
              app.given = res.given
              app.bank = res.bank
              app.date = res.date
              app.description = res.content
              if (res.tags) {
                app.tag = res.tags
                app.more = true
              }
              app.relations = res.relations
              app.render = true
            })
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      sendToPos () {
        var app = this
        if (!this.$refs.form.validate()) return false
        app.lodingText = app.$t('waitingTogetDataFromPOS')
        app.isLoading = true
        app.loadingDialog = true
        const socket = new WebSocket('ws://127.0.0.1:4022/PardakhtNovin_S800_Payment')
        socket.onopen = function (event) {
          socket.send(app.amount)
        }
        socket.onmessage = function (event) {
          var data = JSON.parse(event.data)
          if (data.ResponseCode !== '00') {
            app.isLoading = false
            app.lodingText = null
            app.loadingDialog = false
            app.snakbarText = data.description
            app.snakbarColor = 'pink'
            app.snakbar = true
          } else {
            app.paymentByPos = 'active'
            app.create()
          }
          socket.close()
        }
      },
      clearForm () {
        this.more = true
        this.paymentByPos = null
        this.defaultCurrency = null
        this.accountGiven = null
        this.loadingDialog = false
        this.lodingText = null
        this.bankGiven = null
        this.attachments = null
        this.id = null
        this.extra = false
        this.relations = null
        this.snakbar = null
        this.snakbarText = null
        this.snakbarColor = null
        this.sumRelations = null
        this.currency_amount = 1
        this.tag = null
        this.remianBankColor = 'blue'
        this.remianColor = 'blue'
        this.customerId = null
        this.bankRemain = null
        this.taxStatus = null
        this.clearing = null
        this.snackbarShow = null
        this.amount = 0
        this.editPrice = 0
        this.type = null
        this.NetPrice = 0
        this.taxAmount = 0
        this.tax = 9
        this.discount = 0
        this.taxFeilds = null
        this.discountFeilds = null
        this.account = null
        this.detail = null
        this.bank = null
        this.remianLable = null
        this.description = null
        // this.date = null
        this.$refs.form.resetValidation()
      },
    },
  }
</script>

<style scoped lang="scss">
/* One opaque frame: toolbar → body (soft blue, only rows scroll) → footer.
   Opaque so the theme wallpaper never shows between sections. */
.cmp-page {
  background: #fff !important;
  border-radius: 6px;
  overflow: hidden;
  ::v-deep .v-input { height: auto !important; }
  ::v-deep .app-form-toolbar { border-bottom: 1px solid rgba(15, 23, 42, .07); }
}

.cmp-form {
  display: flex;
  flex-direction: column;
}

.cmp-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: 10px 12px;
  background: #f3f7fc;
  display: flex;
  flex-direction: column;
  gap: 8px;
  > * { flex-shrink: 0; }
  > .cmp-items {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
}

.cmp-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, .07);
  border-radius: 6px;
}

/* ---- Header card ---- */
.cmp-head {
  height: auto;
  overflow: visible;
  border-radius: 6px;
  box-shadow: none;
  .app-form-card__icon { border-radius: 6px; }
  .app-form-card__header { padding: 10px 14px 4px; }
  .app-form-card__body { overflow: visible; padding: 9px 10px 2px; }
  ::v-deep {
    .app-form-fields > .col,
    .app-form-fields > [class*="col-"] {
      padding-top: 0 !important;
      padding-bottom: 1px !important;
      padding-left: 6px !important;
      padding-right: 6px !important;
    }
    .app-form-fields .v-input {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }
    .app-form-fields .v-input__slot {
      margin-bottom: 0 !important;
    }
    .app-form-fields .v-text-field__details,
    .app-form-fields .v-messages {
      min-height: 0 !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden;
    }
    .app-form-fields .v-input.error--text .v-text-field__details,
    .app-form-fields .v-input.error--text .v-messages {
      height: auto !important;
      min-height: 14px !important;
      padding-top: 2px !important;
      overflow: visible;
    }
    .app-form-fields .v-text-field--outlined .v-input__slot,
    .app-form-fields .v-select.v-text-field--outlined .v-input__slot,
    .app-form-fields .v-text-field--outlined.v-input--dense > .v-input__control > .v-input__slot {
      min-height: 32px !important;
      height: 32px !important;
    }
    .app-form-fields .v-text-field--outlined.v-input--dense .v-label {
      font-size: .8rem;
    }
  }
}

/* ---- Totals ---- */
.cmp-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.cmp-kpi {
  --c: 25, 118, 210;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px 10px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, .08);
  border-inline-start: 3px solid rgb(var(--c));
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .05);
  &--green { --c: 46, 160, 67; }
  &--amber { --c: 217, 140, 20; }
  &--blue { --c: 25, 118, 210; }
}

.cmp-kpi__icon {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--c), .12);
  ::v-deep .v-icon { color: rgb(var(--c)) !important; }
}

.cmp-kpi__text {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.cmp-kpi__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.cmp-kpi__label,
.cmp-kpi__meta {
  font-size: .7rem;
  line-height: 1.2;
  opacity: .6;
}

.cmp-kpi__meta {
  flex: 0 0 auto;
  white-space: nowrap;
}

.cmp-kpi__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cmp-kpi__value {
  font-size: .92rem;
  font-weight: 700;
  line-height: 1.25;
  text-align: start;
  unicode-bidi: plaintext;
  small { font-weight: 500; font-size: .7rem; opacity: .7; }
}

/* ---- Tabs ---- */
.cmp-items { padding: 10px 14px 12px; }

.cmp-items .cmp-tabs {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  ::v-deep > .v-tabs-bar {
    flex: 0 0 auto;
    background: rgba(15, 23, 42, .04) !important;
    border-radius: 6px;
    padding: 2px;
    height: 36px !important;
  }
  ::v-deep .v-tab {
    height: 32px !important;
    min-height: 32px !important;
    padding: 0 12px;
    font-size: .82rem;
  }
  ::v-deep .v-tabs-items {
    flex: 1 1 auto;
    min-height: 0;
    background: transparent !important;
    margin-top: 8px;
  }
  ::v-deep .v-window__container,
  ::v-deep .v-window-item { height: 100%; }
}

.cmp-tab {
  border-radius: 6px;
  min-width: 96px;
  letter-spacing: 0;
  text-transform: none;
  font-weight: 500;
  color: rgba(0, 0, 0, .6) !important;
  &.v-tab--active {
    background: #fff;
    color: var(--v-primary-base, #1976d2) !important;
    box-shadow: 0 1px 3px rgba(15, 23, 42, .1);
  }
  &::before { border-radius: 6px; }
}

.cmp-tab__count {
  margin-inline-start: 6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 6px;
  font-size: .68rem;
  line-height: 18px;
  text-align: center;
  background: rgba(25, 118, 210, .12);
}

/* ---- Item rows (rendered by the partials) ---- */
.cmp-page ::v-deep {
  .cmp-list {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .cmp-list__bar {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }
  .cmp-list__scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-inline-end: 4px;
    > * { flex-shrink: 0; }
  }
  .cmp-list__hint { font-size: .78rem; opacity: .6; }
  .cmp-list__add.v-btn {
    border-radius: 6px;
    letter-spacing: 0;
    text-transform: none;
    background: rgba(25, 118, 210, .08);
  }
  .cmp-row {
    display: flex;
    gap: 8px;
    padding: 8px 10px 2px;
    margin-bottom: 8px;
    border: 1px solid rgba(15, 23, 42, .08);
    border-radius: 6px;
    background: #fafbfd;
  }
  .cmp-row__num {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    margin-top: 8px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: .75rem;
    font-weight: 700;
    background: rgba(25, 118, 210, .1);
    color: var(--v-primary-base, #1976d2);
  }
  .cmp-row__fields {
    flex: 1 1 auto;
    min-width: 0;
    /* Override global .app-form-fields spacing for compact item rows */
    .app-form-fields > .col,
    .app-form-fields > [class*="col-"] {
      padding-top: 0 !important;
      padding-bottom: 9px !important;
      padding-left: 6px !important;
      padding-right: 6px !important;
    }
    .app-form-fields .v-input {
      margin-top: 0 !important;
      margin-bottom: 0 !important;
    }
    .app-form-fields .v-input__slot {
      margin-bottom: 0 !important;
    }
    /* Collapse reserved validation strip (main gap between field rows) */
    .app-form-fields .v-text-field__details,
    .app-form-fields .v-messages {
      min-height: 0 !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden;
    }
    .app-form-fields .v-input.error--text .v-text-field__details,
    .app-form-fields .v-input.error--text .v-messages {
      height: auto !important;
      min-height: 14px !important;
      padding-top: 2px !important;
      overflow: visible;
    }
    .app-form-fields .v-text-field--outlined .v-input__slot,
    .app-form-fields .v-select.v-text-field--outlined .v-input__slot,
    .app-form-fields .v-text-field--outlined.v-input--dense > .v-input__control > .v-input__slot {
      min-height: 32px !important;
      height: 32px !important;
    }
    .app-form-fields .v-text-field--outlined.v-input--dense .v-label {
      font-size: .8rem;
    }
  }
  .cmp-row__remove {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    margin-top: 6px;
    border: 0;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(233, 30, 99, .08);
    cursor: pointer;
    &:hover { background: rgba(233, 30, 99, .16); }
  }
  .cmp-empty {
    height: 100%;
    min-height: 90px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1.5px dashed rgba(15, 23, 42, .12);
    border-radius: 6px;
    font-size: .82rem;
    opacity: .7;
  }
  .cmp-header-remain {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: 32px;
    margin-top: 0;
    padding: 0 10px;
    border-radius: 6px;
    background: rgba(25, 118, 210, .06);
    font-size: .78rem;
    white-space: nowrap;
    strong { unicode-bidi: plaintext; }
  }
}

/* ---- Footer ---- */
.cmp-footer {
  flex: 0 0 auto;
  background: #fff;
  border-top: 1px solid rgba(15, 23, 42, .07);
  padding: 8px 14px !important;
}

.cmp-footer__total {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: .8rem;
  span { opacity: .6; }
  strong { font-size: 1.05rem; unicode-bidi: plaintext; }
  small { opacity: .6; }
}

/* ---- Mobile ---- */
.cmp-page--mobile {
  .cmp-body { padding: 10px 8px; overflow: visible; }
  .cmp-kpis { grid-template-columns: 1fr 1fr; }
  .cmp-kpi--blue { grid-column: 1 / -1; }
  .cmp-items, .cmp-items .cmp-tabs { display: block; }
  .cmp-items { padding: 10px; }
  .cmp-tab { flex: 1 1 0; min-width: 0; }
  ::v-deep .cmp-list__scroll { overflow: visible; }
  ::v-deep .cmp-row { flex-wrap: wrap; }
  .cmp-footer {
    position: sticky;
    bottom: 0;
    z-index: 3;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom)) !important;
    .app-form-action-btn { flex: 1 1 auto; height: 46px !important; }
  }
}

/* ---- Dark ---- */
.cmp-page.theme--dark {
  background: #1e1e1e !important;
  .cmp-body { background: #161a20; }
  .cmp-card { background: #1e1e1e; border-color: rgba(255, 255, 255, .1); }
  .cmp-kpi { background: rgba(255, 255, 255, .04); border-color: rgba(255, 255, 255, .12); box-shadow: none; }
  .cmp-items .cmp-tabs ::v-deep > .v-tabs-bar { background: rgba(255, 255, 255, .06) !important; }
  .cmp-tab { color: rgba(255, 255, 255, .7) !important; }
  .cmp-tab.v-tab--active { background: rgba(255, 255, 255, .1); }
  .cmp-footer { background: #1e1e1e; border-top-color: rgba(255, 255, 255, .1); }
  ::v-deep .cmp-row { background: rgba(255, 255, 255, .03); border-color: rgba(255, 255, 255, .1); }
  ::v-deep .cmp-empty { border-color: rgba(255, 255, 255, .2); }
}
</style>
